import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Map, { NavigationControl } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import Boundaries from './Boundaries';
import HexMap from './HexMap';
import OutletMarkers from './OutletMarkers';
import Filter from './Filter';
import OutletCard from './OutletCard';
import SearchBox from './SearchBox';
import PostcodeSearch from './PostcodeSearch';
import OutletSearchBox from './OutletSearchBox';
import ConstituencyLabel from './ConstituencyLabel';
import MapLegend from './MapLegend';
import { colours } from './colours';
import arrow from './arrow-back.svg';

const MapView = ({
    outlets,
    filteredOutlets,
    setFilteredOutlets,
    activeConstituency,
    setActiveConstituency,
    selectedOutlet,
    setSelectedOutlet,
    boundaryType,
    setBoundaryType,
    ladStats,
    constituencyData,
    ladData,
    statsData,
    hexLayoutData
}) => {
    const [hoverConstituency, setHoverConstituency] = useState(null);
    const [hoverOutlet, setHoverOutlet] = useState(null);
    const [mapBounds, setMapBounds] = useState(null);
    const [clusterActive, setClusterActive] = useState(null);
    const [searchMode, setSearchMode] = useState(null); // 'geography', 'outlet', or null
    const [viewMode] = useState('geographic'); // 'geographic' or 'hex'
    const [mapMetric, setMapMetric] = useState('total'); // 'total' or 'per100k'
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const [selectedMedia, setSelectedMedia] = useState([]);
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [selectedOwner, setSelectedOwner] = useState(null);

    const mapRef = useRef();
    const outletRefs = useRef({});

    const { idKey, nameKey, coverageField, currentStats } = useMemo(() => {
        return {
            idKey: boundaryType === 'constituency' ? 'PCON24CD' : 'LAD24CD',
            nameKey: boundaryType === 'constituency' ? 'PCON24NM' : 'LAD24NM',
            coverageField: boundaryType === 'constituency' ? 'COVERAGE_CONSTITUENCY' : 'COVERAGE_LAD',
            currentStats: boundaryType === 'constituency' ? statsData : ladStats
        };
    }, [boundaryType, ladStats, statsData]);

    const districtLookup = useMemo(() => {
        const lookup = {};
        const data = boundaryType === 'constituency' ? constituencyData : ladData;

        if (data && data.features) {
            data.features.forEach(feature => {
                lookup[feature.properties[idKey]] = feature.properties[nameKey];
            });
        }
        return lookup;
    }, [boundaryType, idKey, nameKey, constituencyData, ladData]);

    const goToConstituency = useCallback((constituencyProperties) => {
        if (!constituencyProperties) {
            setActiveConstituency(null);
            setSelectedOutlet(null);
            setSearchMode(null);
            if (mapRef.current) {
                mapRef.current.easeTo({ center: [-4, 55.7], zoom: 5 });
            }
            return;
        }

        setSearchMode('geography');
        setActiveConstituency(constituencyProperties);
        setSelectedOutlet(null);

        const { minLng, minLat, maxLng, maxLat } = constituencyProperties;
        if (mapRef.current) {
            mapRef.current.fitBounds(
                [[minLng, minLat], [maxLng, maxLat]],
                { padding: 40, duration: 1000 }
            );
        }
    }, [setActiveConstituency, setSelectedOutlet]);

    const goToOutletCoverage = useCallback((outlet) => {
        setSearchMode('outlet');
        setSelectedOutlet(outlet);
        setActiveConstituency(null);

        const coverage = outlet[coverageField] || [];
        const boundaries = boundaryType === 'constituency' ? constituencyData : ladData;
        const features = (boundaries?.features || []).filter(f => coverage.includes(f.properties[idKey]));

        if (features.length === 0) return;

        let minLng, minLat, maxLng, maxLat;

        features.forEach(feature => {
            if (!feature || !feature.geometry) return;
            const coords = feature.geometry.type === "Polygon"
                ? [feature.geometry.coordinates[0]]
                : (feature.geometry.type === "MultiPolygon" ? feature.geometry.coordinates.map(p => p[0]) : []);

            if (!coords || coords.length === 0) return;

            coords.forEach(ring => {
                ring.forEach(coord => {
                    if (!minLng || minLng > coord[0]) minLng = coord[0];
                    if (!minLat || minLat > coord[1]) minLat = coord[1];
                    if (!maxLng || maxLng < coord[0]) maxLng = coord[0];
                    if (!maxLat || maxLat < coord[1]) maxLat = coord[1];
                });
            });
        });

        if (mapRef.current && minLng) {
            mapRef.current.fitBounds(
                [[minLng, minLat], [maxLng, maxLat]],
                { padding: 60, duration: 1000 }
            );
        }
    }, [boundaryType, coverageField, idKey, constituencyData, ladData, setActiveConstituency, setSelectedOutlet]);

    const handleConstituencyHover = (event) => {
        if (event.features && event.features[0]) {
            setHoverConstituency(event.features[0].properties);
        } else {
            setHoverConstituency(null);
        }
    };

    const handleHexHover = useCallback((hex, event) => {
        if (hex) {
            setHoverConstituency({
                [idKey]: hex.id,
                [nameKey]: hex.n
            });
            if (event) {
                const rect = event.currentTarget.closest('#map-container').getBoundingClientRect();
                setMousePos({
                    x: event.clientX - rect.left,
                    y: event.clientY - rect.top
                });
            }
        } else {
            setHoverConstituency(null);
        }
    }, [idKey, nameKey]);

    const handleHexClick = useCallback((hex) => {
        goToConstituency({
            [idKey]: hex.id,
            [nameKey]: hex.n
        });
    }, [goToConstituency, idKey, nameKey]);

    const calculateNaturalBreaks = useCallback((data, nClasses = 5) => {
        const values = data.filter(v => v > 0).sort((a, b) => a - b);

        // Guard: not enough distinct positive values for meaningful breaks
        if (values.length < nClasses || values[0] === values[values.length - 1]) {
            return colours.SCALE_100K;
        }

        // Simplistic Jenks / Head-Tail / K-Means style clustering for breaks
        const getBreaks = (data, n) => {
            if (data.length <= n) return data;

            const mat1 = Array(data.length + 1).fill(0).map(() => Array(n + 1).fill(0));
            const mat2 = Array(data.length + 1).fill(0).map(() => Array(n + 1).fill(0));

            for (let i = 1; i <= n; i++) {
                mat1[1][i] = 1;
                mat2[1][i] = 0;
                for (let j = 2; j <= data.length; j++) mat2[j][i] = Infinity;
            }

            let v = 0;
            for (let l = 2; l <= data.length; l++) {
                let s1 = 0;
                let s2 = 0;
                let w = 0;
                for (let m = 1; m <= l; m++) {
                    const i3 = l - m + 1;
                    const val = data[i3 - 1];
                    s1 += val;
                    s2 += val * val;
                    w++;
                    v = s2 - (s1 * s1) / w;

                    if (i3 !== 1) {
                        for (let j = 2; j <= n; j++) {
                            if (mat2[l][j] >= (v + mat2[i3 - 1][j - 1])) {
                                mat1[l][j] = i3;
                                mat2[l][j] = v + mat2[i3 - 1][j - 1];
                            }
                        }
                    }
                }
                mat1[l][1] = 1;
                mat2[l][1] = v;
            }

            const kIndices = Array(n).fill(0);
            kIndices[n - 1] = data.length;
            let currentK = data.length;
            for (let j = n; j >= 2; j--) {
                const id = mat1[currentK][j] - 1;
                kIndices[j - 2] = id;
                currentK = id;
            }

            return kIndices.map(idx => data[idx - 1]);
        };

        const breaks = getBreaks(values, nClasses);

        // Guard: if breaks contain undefined or non-ascending values, use fallback
        if (!breaks || breaks.some(b => b === undefined || isNaN(b))) {
            return colours.SCALE_100K;
        }

        // De-duplicate and ensure strictly ascending
        const uniqueBreaks = [...new Set(breaks)].sort((a, b) => a - b);
        if (uniqueBreaks.length < 2) {
            return colours.SCALE_100K;
        }

        const colors = ['#CBE6FF', '#96C2FF', '#375E92', '#1A365D', '#0A1A2F'];

        const bins = [{ min: 0, max: 0, color: '#ffffff', label: '0' }];
        let prevBreak = 0;

        uniqueBreaks.forEach((brk, i) => {
            if (i >= colors.length) return; // Don't exceed available colors
            const min = i === 0 ? 0.1 : prevBreak + 0.01;
            bins.push({
                min: min,
                max: brk,
                color: colors[i],
                label: i === 0 ? `0.1-${brk.toFixed(1)}` : `${min.toFixed(1)}-${brk.toFixed(1)}`
            });
            prevBreak = brk;
        });

        // Ensure the last bin goes to Infinity
        if (bins.length > 1) {
            bins[bins.length - 1].max = Infinity;
            const lastMin = uniqueBreaks.length >= 2 ? uniqueBreaks[uniqueBreaks.length - 2] + 0.01 : 0.1;
            bins[bins.length - 1].label = `${lastMin.toFixed(1)}+`;
        }

        return bins;
    }, []);


    const dynamicScale100k = useMemo(() => {
        const currentStats = boundaryType === 'constituency' ? statsData : ladStats;
        const coverageKey = boundaryType === 'constituency' ? 'COVERAGE_CONSTITUENCY' : 'COVERAGE_LAD';
        const idKey = boundaryType === 'constituency' ? 'PCON24CD' : 'LAD24CD';

        const values = currentStats.map(stat => {
            const id = stat.id || stat[idKey];
            const outletsInBoundary = filteredOutlets.filter(outlet => outlet[coverageKey]?.includes(id));
            const pop = stat.pop || stat.POPULATION;
            return pop ? (outletsInBoundary.length / (pop / 100000)) : 0;
        });

        return calculateNaturalBreaks(values);
    }, [boundaryType, ladStats, statsData, filteredOutlets, calculateNaturalBreaks]);

    useEffect(() => {
        if (selectedOutlet) {
            const outletCard = outletRefs.current[selectedOutlet.ID];
            if (outletCard) {
                outletCard.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }
        }
    }, [selectedOutlet]);

    // When an outlet search has no selected outlet, exit search mode
    // This handles the case where the tooltip close button clears selectedOutlet
    useEffect(() => {
        if (searchMode === 'outlet' && !selectedOutlet) {
            setSearchMode(null);
        }
    }, [searchMode, selectedOutlet]);

    const renderMapControls = () => (
        <div className="map-controls-overlay">
            <div className="toggle-wrapper">
                <p className="toggle-header">Colouring</p>
                <div className="view-toggle">
                    <button
                        className={mapMetric === 'total' ? 'active' : ''}
                        onClick={() => setMapMetric('total')}
                    >
                        N Outlets
                    </button>
                    <button
                        className={mapMetric === 'per100k' ? 'active' : ''}
                        onClick={() => setMapMetric('per100k')}
                    >
                        Per 100k
                    </button>
                </div>
            </div>
        </div>
    );

    const renderFilterOverlay = () => (
        <div className="filter-overlay">
            <Filter
                outlets={outlets}
                setFilteredOutlets={setFilteredOutlets}
                selectedMedia={selectedMedia}
                setSelectedMedia={setSelectedMedia}
                selectedTypes={selectedTypes}
                setSelectedTypes={setSelectedTypes}
                selectedOwner={selectedOwner}
                setSelectedOwner={setSelectedOwner}
            />
        </div>
    );

    const resetFilters = () => {
        setSelectedMedia([]);
        setSelectedTypes([]);
        setSelectedOwner(null);
        goToConstituency(null);
    };

    const renderFilterReminder = () => {
        const activeFilters = [];
        if (selectedMedia.length > 0) activeFilters.push(`${selectedMedia.length} Media`);
        if (selectedTypes.length > 0 && selectedTypes.length < 2) activeFilters.push(selectedTypes[0]);
        if (selectedOwner) activeFilters.push(`Owner: ${selectedOwner}`);

        if (activeFilters.length === 0 && !searchMode) return null;

        return (
            <div className="filter-reminder-bar">
                <div className="filter-info">
                    {activeFilters.length > 0 ? (
                        <span>Filtered by: <b>{activeFilters.join(', ')}</b></span>
                    ) : (
                        <span>Viewing: <b>{searchMode === 'geography' ? activeConstituency[nameKey] : selectedOutlet?.TITLE}</b></span>
                    )}
                </div>
                <button className="reset-button" onClick={resetFilters}>Reset View</button>
            </div>
        );
    };


    const areaRanks = useMemo(() => {
        if (!filteredOutlets.length || !currentStats.length) return [];
        return currentStats.map(stat => {
            const areaOutlets = filteredOutlets.filter(o => o[coverageField]?.includes(stat.id));
            const popNormalized = stat.pop || 75000;
            const per100k = (areaOutlets.length / (popNormalized / 100000));
            return { id: stat.id, per100k };
        }).sort((a, b) => b.per100k - a.per100k);
    }, [filteredOutlets, currentStats, coverageField]);

    const getOrdinal = (n) => {
        const s = ["th", "st", "nd", "rd"], v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    const visibleOutlets = searchMode === 'geography' && activeConstituency
        ? filteredOutlets.filter(outlet => outlet[coverageField]?.includes(activeConstituency[idKey]))
        : searchMode === 'outlet' && selectedOutlet
            ? filteredOutlets.filter(outlet => outlet.ID === selectedOutlet.ID)
            : filteredOutlets;

    const getStorytellingSummary = () => {
        if (searchMode === 'geography' && activeConstituency) {
            const idyCount = visibleOutlets.filter(o => o.INDEPENDENCE).length;
            const corpCount = visibleOutlets.length - idyCount;

            const currentStat = areaRanks.find(r => r.id === activeConstituency[idKey]);
            const rankPosition = areaRanks.findIndex(r => r.id === activeConstituency[idKey]) + 1;

            const per100k = currentStat ? currentStat.per100k.toFixed(2) : '0.00';
            const rankingDesc = rankPosition > 0
                ? `placing ${activeConstituency[nameKey]} as the ${getOrdinal(rankPosition)} best served ${boundaryType === 'lad' ? 'LAD' : 'constituency'} nationally.`
                : '';

            return (
                <div className="storytelling-summary">
                    <p>There are <b>{visibleOutlets.length}</b> local news titles serving this {boundaryType === 'lad' ? 'LAD' : 'constituency'}.
                        {' '}Adjusted by population, this means there are <b>{per100k}</b> outlets per 100,000 residents, {rankingDesc}
                        <b> {idyCount}</b> are independently owned, and <b>{corpCount}</b> are part of corporate groups.</p>
                </div>
            );
        }

        if (searchMode === 'outlet' && selectedOutlet) {
            const coverageCount = selectedOutlet[coverageField]?.length || 0;
            return (
                <div className="storytelling-summary">
                    <p><b>{selectedOutlet.TITLE}</b> provides coverage across <b>{coverageCount}</b> {boundaryType}s.
                        It is <b>{selectedOutlet.INDEPENDENCE ? 'independently' : 'corporately'}</b> owned by <b>{selectedOutlet.OWNER}</b>.</p>
                </div>
            );
        }
        return null;
    };

    const outletsToShowOnMap = searchMode
        ? (selectedOutlet && !visibleOutlets.includes(selectedOutlet)
            ? [...visibleOutlets, selectedOutlet]
            : visibleOutlets)
        : [];

    return (
        <div className="view-container">
            <div id="sidebar">
                <div id="scrollable-area">
                    <div id="search-grid">
                        <SearchBox
                            goToConstituency={goToConstituency}
                            boundaryType={boundaryType}
                            boundaries={boundaryType === 'constituency' ? constituencyData : ladData}
                        />
                        <PostcodeSearch
                            goToConstituency={goToConstituency}
                            boundaries={boundaryType === 'constituency' ? constituencyData : ladData}
                            idKey={boundaryType === 'constituency' ? 'PCON24CD' : 'LAD24CD'}
                            boundaryType={boundaryType}
                        />
                    </div>
                    <OutletSearchBox
                        outlets={outlets}
                        goToOutletCoverage={goToOutletCoverage}
                    />

                    <h1>
                        {searchMode && <img className='arrow' src={arrow} alt="back to all" onClick={() => goToConstituency(null)} />}
                        {(searchMode === 'geography' && activeConstituency) ? activeConstituency[nameKey] : (searchMode === 'outlet' && selectedOutlet) ? selectedOutlet.TITLE : "All"}
                    </h1>
                    {getStorytellingSummary()}
                    <section id="outlet-list-container">
                        {visibleOutlets.map(outlet => (
                            <OutletCard
                                key={outlet.ID}
                                innerRef={(element) => outletRefs.current[outlet.ID] = element}
                                hoverOutlet={hoverOutlet}
                                setHoverOutlet={setHoverOutlet}
                                selectedOutlet={selectedOutlet}
                                setSelectedOutlet={setSelectedOutlet}
                                districtLookup={districtLookup}
                                outlet={outlet}
                                boundaryType={boundaryType}
                            />
                        ))}
                    </section>
                </div>
            </div>
            <div id="map-container" style={{ position: 'relative', borderRadius: "1rem", overflow: 'hidden' }}>
                {renderFilterReminder()}
                <div className="legend-controls-wrapper">
                    <MapLegend mapMetric={mapMetric} dynamicScale={dynamicScale100k} />
                    {renderMapControls()}
                </div>
                {renderFilterOverlay()}

                {viewMode === 'geographic' ? (
                    <Map
                        ref={mapRef}
                        minZoom={5}
                        initialViewState={{
                            longitude: -4,
                            latitude: 55.7,
                            zoom: 5
                        }}
                        mapStyle="mapbox://styles/kingmob/cltyll6m600hy01qsh9kk19ky"
                        mapboxAccessToken="pk.eyJ1Ijoic2ltb25hYmlzaWFuaSIsImEiOiJjbWV6eXIxbGUxc3U0MmpxeGtycG9nMWZhIn0.d3Zf57jIiUIwZRn-qoN7bA"
                        onMouseMove={handleConstituencyHover}
                        onClick={event => {
                            !event.originalEvent.defaultPrevented && event.features[0] && goToConstituency(event.features[0].properties);
                        }}
                        onMove={() => {
                            if (mapRef.current) {
                                setMapBounds(mapRef.current.getBounds());
                            }
                        }}
                        interactiveLayerIds={['data']}
                        style={{ width: '100%', height: '100%', position: 'relative' }}
                    >
                        <Boundaries
                            outlets={filteredOutlets}
                            activeConstituency={activeConstituency}
                            selectedOutlet={selectedOutlet}
                            searchMode={searchMode}
                            mapMetric={mapMetric}
                            boundaryType={boundaryType}
                            ladStats={ladStats}
                            boundaries={boundaryType === 'constituency' ? constituencyData : ladData}
                            statsData={statsData}
                            dynamicScale={dynamicScale100k}
                        />

                        {outletsToShowOnMap.length > 0 && <OutletMarkers
                            outlets={outletsToShowOnMap}
                            hoverOutlet={hoverOutlet}
                            setHoverOutlet={setHoverOutlet}
                            activeConstituency={activeConstituency}
                            selectedOutlet={selectedOutlet}
                            setSelectedOutlet={setSelectedOutlet}
                            mapBounds={mapBounds}
                            clusterActive={clusterActive}
                            setClusterActive={setClusterActive}
                            boundaryType={boundaryType}
                            boundaries={boundaryType === 'constituency' ? constituencyData : ladData}
                        />}



                        <NavigationControl position="bottom-right" />
                    </Map>
                ) : (
                    <div className="hex-map-view">
                        <HexMap
                            activeConstituency={activeConstituency}
                            selectedOutlet={selectedOutlet}
                            searchMode={searchMode}
                            outlets={filteredOutlets}
                            mapMetric={mapMetric}
                            boundaryType={boundaryType}
                            dynamicScale={dynamicScale100k}
                            hexData={hexLayoutData}
                            statsData={statsData}
                            onHexClick={handleHexClick}
                            onHexHover={handleHexHover}
                        />
                    </div>
                )}

                {hoverConstituency && !activeConstituency && (
                    <ConstituencyLabel
                        hoverConstituency={hoverConstituency}
                        outlets={filteredOutlets}
                        isHex={viewMode !== 'geographic'}
                        mousePos={mousePos}
                        boundaryType={boundaryType}
                    />
                )}
            </div>
        </div>
    );
};

export default MapView;
